import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';

// Import Pages
import LoginPage from './pages/LoginPage';
import ChatPage from './pages/ChatPage';

// Import Components
import PrivateRoute from './components/PrivateRoute';
import TitleBar from './components/TitleBar';
import PwaInstallPrompt from './components/PwaInstallPrompt';
import IosInstallPrompt from './components/IosInstallPrompt';
import DesktopDownloadPrompt from './components/DesktopDownloadPrompt';

// Import Hooks
import { useIosDetection } from './hooks/useIosDetection';
import { useOsDetection } from './hooks/useOsDetection';

// Import Global Styles
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/App.css';


// --- START: UPDATED HELPER FUNCTION TO PERSIST WIDGET MODE ---
const getAppMode = () => {
    // 1. Check the URL query parameter first. This is the initial entry point.
    const queryParams = new URLSearchParams(window.location.search);
    const urlMode = queryParams.get('mode');

    if (urlMode === 'widget') {
        // If the URL says it's a widget, SAVE this fact in sessionStorage and return.
        sessionStorage.setItem('appMode', 'widget');
        return 'widget';
    }

    // 2. If no URL parameter, check sessionStorage. This handles redirects inside the iframe.
    const sessionMode = sessionStorage.getItem('appMode');
    if (sessionMode === 'widget') {
        return 'widget';
    }
    
    // 3. Check for Electron as before.
    if (window.electronAPI) {
        return 'electron';
    }

    // 4. Default to 'web' if none of the above are true.
    return 'web';
};
// --- END: UPDATED HELPER FUNCTION ---


function App() {
    // --- START: MODIFIED STATE LOGIC ---
    // Initialize state directly from the function call
    const [appMode, setAppMode] = useState(getAppMode()); 
    
    // This logic remains the same
    const { isIos } = useIosDetection();
    const os = useOsDetection();
    const isDesktop = (os === 'Windows' || os === 'macOS');

    useEffect(() => {
        // The mode is already set, so we just need to add the class
        document.body.classList.add(`${appMode}-view`);
    }, [appMode]); // Run effect if appMode ever changes

    // Create boolean flags from the appMode for easier use in JSX
    const isElectron = appMode === 'electron';
    const isWidget = appMode === 'widget';
    const isWeb = appMode === 'web';
    // --- END: MODIFIED STATE LOGIC ---

    return (
        <BrowserRouter>
            <AuthProvider>
                
                {/* Conditionally render the custom title bar ONLY for Electron */}
                {isElectron && <TitleBar />}

                {/* The container now dynamically changes its class based on the mode. */}
                <div className={isWidget ? 'widget-view' : 'mobile-view-container'}>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route 
                            path="/" 
                            element={
                                <PrivateRoute>
                                    {/* --- PASS THE isWidget PROP --- */}
                                    {/* This is needed so the Header inside ChatPage knows when to show the close button */}
                                    <ChatPage isWidget={isWidget} />
                                </PrivateRoute>
                            } 
                        />
                    </Routes>
                </div>
                
                {/* The install prompts should ONLY show in the main 'web' mode. */}
                {isWeb && (
                    <>
                        {isIos && <IosInstallPrompt />}
                        {!isIos && <PwaInstallPrompt />}
                        {isDesktop && <DesktopDownloadPrompt />}
                    </>
                )}

            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;